import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useOsintStream from './useOsintStream';
import { usePersonCategoryFilter } from '../modules/Person/PersonDiscovery';
import { buildAppendMergeLabel, getStreamMergeIdentity } from '../utils/streamMergeUtils';
import {
  computeStats,
  countStreamIntelRows,
  createInitialTabStorage,
  filterStreamIntelRows,
  filterStreamLogRows,
  getDisplayNameForResult,
  mergePersonTagLists,
  patchTabStorageForQuery,
  isPersonCensysResult,
  pickIntelRowsForDisplay,
  reviewResultKey,
  STREAM_CHAT_ID,
  TAB_COMPANY,
  TAB_EMAIL,
  TAB_PERSON,
  TAB_PHONE,
  validateTabInputValue,
} from '../utils/investigationWorkspaceUtils';
import {
  buildPersonStreamParams,
  buildPersonStreamParamsFromResolved,
  shouldReusePersonPreprocess,
} from '../utils/personSearchResolve';
import { checkDomainNS, emailDomainFromAddress } from '../utils/checkDomainNS';
import { CORPORATE_DOMAIN_DNS_HINT, CORPORATE_DOMAIN_REQUIRED_HINT, EMAIL_DOMAIN_DNS_HINT, SINGLE_TERM_INSUFFICIENT_HINT, companyDomainFromQuery, isInsufficientSingleTermQuery, looksLikeSystemCommandOrCode, SYSTEM_COMMAND_MESSAGE } from '../pages/home/homeSearchUtils';
import { inferCompanyCountryFromDomain } from '../pages/home/companyQueryParse';
import {
  deepLinkOptsFromPivot,
  openInvestigationInNewTab,
} from '../utils/investigationDeepLink';
import {
  beginSearchTrace,
  clearActiveSearchTrace,
  getActiveSearchTraceId,
  markSearchTrace,
  setActiveSearchTraceMeta,
} from '../utils/searchTraceClient';
import { trackEvent } from '../utils/analytics';

export default function useInvestigationWorkspace() {
  const { activeFilter: personCategoryFilter, setFilter: setPersonCategoryFilter, resetFilter: resetPersonCategoryFilter } =
    usePersonCategoryFilter();
  const [activeTab, setActiveTab] = useState(TAB_PERSON);
  const [isSearchingInTab, setIsSearchingInTab] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [feedHiddenKeys, setFeedHiddenKeys] = useState(() => new Set());
  const [pendingScan, setPendingScan] = useState(false);
  const [tabStorage, setTabStorage] = useState(createInitialTabStorage);

  const { results, loading, completed, completionData, startStream, cancelStream, abortScan, resetStream } = useOsintStream();
  const logEndRef = useRef(null);
  const lastStreamMergeIdentityRef = useRef({ person: null, email: null, phone: null, company: null });
  const scanCompletePulseRef = useRef(false);
  const resetArmedRef = useRef({ person: false, email: false, phone: false, company: false });
  /** Hero GO preprocess result — reused by handleSearch to avoid a second POST /search/preprocess. */
  const personStreamPrefetchRef = useRef(null);

  const handleTabChange = useCallback(
    (tabId) => {
      if (loading) cancelStream();
      resetStream();
      setIsSearchingInTab(false);
      resetPersonCategoryFilter();
      resetArmedRef.current[tabId] = false;
      setActiveTab(tabId);
    },
    [loading, cancelStream, resetStream, resetPersonCategoryFilter],
  );

  useEffect(() => {
    if (activeTab !== TAB_PERSON) resetPersonCategoryFilter();
  }, [activeTab, resetPersonCategoryFilter]);

  useEffect(() => {
    if (loading) return;
    const logs = tabStorage[activeTab]?.logs;
    if (logEndRef.current && logs?.length) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeTab, tabStorage, loading]);

  useEffect(() => {
    if (!completed) {
      scanCompletePulseRef.current = false;
      return;
    }
    if (completionData?.status === 'cancelled') return;
    if (scanCompletePulseRef.current) return;
    scanCompletePulseRef.current = true;
    markSearchTrace('scan_complete', { detail: completionData?.status || 'completed' });
    clearActiveSearchTrace();
    const id = getStreamMergeIdentity(activeTab, tabStorage);
    if (id) lastStreamMergeIdentityRef.current[activeTab] = id;

    trackEvent('scan_completed', {
      module: activeTab,
      partial: Boolean(completionData?.partial),
      finalize_reason: completionData?.finalize_reason || 'finished',
    });
  }, [completed, activeTab, completionData, tabStorage, results]);

  useEffect(() => {
    const intelRows = filterStreamIntelRows(results);
    const logRows = filterStreamLogRows(results);

    if (completed && !loading) {
      setTabStorage((prev) => ({
        ...prev,
        [activeTab]: {
          ...prev[activeTab],
          results: intelRows,
          logs: logRows,
          completed: true,
        },
      }));
      return;
    }

    if (loading && results.length > 0) {
      setTabStorage((prev) => ({
        ...prev,
        [activeTab]: {
          ...prev[activeTab],
          results: intelRows,
          logs: logRows,
          completed: false,
        },
      }));
    }
  }, [results, completed, activeTab, loading]);

  useEffect(() => {
    if (activeTab !== TAB_PHONE && activeTab !== TAB_PERSON) return;
    const leads = completionData?.extracted_leads;
    if (!leads || typeof leads !== 'object') return;
    const nextLeads = {
      emails: Array.isArray(leads.emails) ? leads.emails : [],
      phones: Array.isArray(leads.phones) ? leads.phones : [],
      cuis: Array.isArray(leads.cuis) ? leads.cuis : [],
      associated_urls: Array.isArray(leads.associated_urls) ? leads.associated_urls : [],
      associated_urls_partial: Array.isArray(leads.associated_urls_partial)
        ? leads.associated_urls_partial
        : [],
      pivots: Array.isArray(leads.pivots) ? leads.pivots : [],
    };
    setTabStorage((prev) => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        extractedLeads: nextLeads,
      },
    }));
  }, [activeTab, completionData]);

  const clearFeedExclusionsForActiveTab = useCallback(() => {
    setFeedHiddenKeys((prev) => {
      const next = new Set();
      const prefix = `${activeTab}:`;
      for (const k of prev) {
        if (!k.startsWith(prefix)) next.add(k);
      }
      return next;
    });
  }, [activeTab]);

  const clearFeedExclusionsForTab = useCallback((tabId) => {
    setFeedHiddenKeys((prev) => {
      const next = new Set();
      const prefix = `${tabId}:`;
      for (const k of prev) {
        if (!k.startsWith(prefix)) next.add(k);
      }
      return next;
    });
  }, []);

  const excludeResultFromFeed = useCallback(
    (r) => {
      setFeedHiddenKeys((prev) => new Set(prev).add(`${activeTab}:${reviewResultKey(r)}`));
    },
    [activeTab],
  );

  const handleResetModule = useCallback(() => {
    resetArmedRef.current[activeTab] = false;
    personStreamPrefetchRef.current = null;
    if (loading) cancelStream();
    clearFeedExclusionsForActiveTab();
    resetStream();
    setIsSearchingInTab(false);
    lastStreamMergeIdentityRef.current[activeTab] = null;
    if (activeTab === TAB_COMPANY) {
      setTabStorage((prev) => ({
        ...prev,
        company: {
          name: '',
          domain: '',
          country: 'GLOBAL',
          locationTags: [],
          results: [],
          logs: [],
          completed: false,
          inputError: '',
        },
      }));
    } else {
      setTabStorage((prev) => ({
        ...prev,
        [activeTab]:
          activeTab === TAB_PERSON
            ? {
                ...prev.person,
                input: '',
                results: [],
                logs: [],
                completed: false,
                searchScope: 'ro',
                inputError: '',
                locationTags: [],
                contextTags: [],
                usernameTags: [],
              }
            : {
                ...prev[activeTab],
                input: '',
                results: [],
                logs: [],
                completed: false,
                inputError: '',
                ...(activeTab === TAB_PHONE
                  ? {
                      searchScope: 'ro',
                      extractedLeads: { emails: [], phones: [], cuis: [], associated_urls: [], associated_urls_partial: [], pivots: [] },
                    }
                  : {}),
              },
      }));
    }
  }, [activeTab, loading, cancelStream, clearFeedExclusionsForActiveTab, resetStream]);

  const handleClearOutputOnly = useCallback(() => {
    if (loading) cancelStream();
    clearFeedExclusionsForActiveTab();
    resetStream();
    setIsSearchingInTab(false);
    lastStreamMergeIdentityRef.current[activeTab] = null;
    setTabStorage((prev) => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        results: [],
        logs: [],
        completed: false,
        ...(activeTab === TAB_PHONE
          ? { extractedLeads: { emails: [], phones: [], cuis: [], associated_urls: [], associated_urls_partial: [], pivots: [] } }
          : {}),
      },
    }));
  }, [activeTab, loading, cancelStream, clearFeedExclusionsForActiveTab, resetStream]);

  const handleSearch = useCallback(async () => {
    if (activeTab === TAB_PERSON) {
      resetPersonCategoryFilter();
    }
    if (!getActiveSearchTraceId()) {
      const tabKey = activeTab;
      const queryHint =
        activeTab === TAB_COMPANY
          ? tabStorage.company.name || tabStorage.company.domain || ''
          : tabStorage[activeTab]?.input || '';
      beginSearchTrace({ query: queryHint, tab: tabKey, source: 'tab_search' });
    }

    let endpoint = '';
    let params = { chat_id: STREAM_CHAT_ID };
    let personResolved = null;
    if (activeTab === TAB_COMPANY) {
      const { name, domain, country } = tabStorage.company;
      if (!name || loading) return;
      endpoint = '/aggregate-company-stream';
      params.name = name;
      if (domain) {
        params.domain = domain;
        params.primary_domain = domain;
      }
      params.country = inferCompanyCountryFromDomain(domain || tabStorage.company.domain) || country;
      if (tabStorage.company.locationTags?.length) {
        params.context_locations = tabStorage.company.locationTags.join(',');
      }
    } else {
      const currentInput = tabStorage[activeTab].input;
      if (!currentInput || loading || tabStorage[activeTab].inputError) return;
      switch (activeTab) {
        case TAB_PERSON: {
          endpoint = '/aggregate-stream-name';
          const prefetch = personStreamPrefetchRef.current;
          personStreamPrefetchRef.current = null;
          if (shouldReusePersonPreprocess(prefetch, currentInput)) {
            Object.assign(params, prefetch.params);
            personResolved = prefetch.resolved;
          } else {
            const built = await buildPersonStreamParams(currentInput, tabStorage.person);
            Object.assign(params, built.params);
            personResolved = built.resolved;
          }
          break;
        }
        case TAB_EMAIL:
          endpoint = '/aggregate-email-stream';
          params.email = currentInput;
          break;
        case TAB_PHONE: {
          endpoint = '/aggregate-phone-stream';
          const rawPhone = String(currentInput || '').trim();
          const allTokenRe = /(?:^|:)\s*all\s*(?:$|:)/i;
          const hasAllToken = allTokenRe.test(rawPhone);
          const normalizedPhone = rawPhone.replace(allTokenRe, '').trim().replace(/^:+|:+$/g, '').trim();
          params.phone = normalizedPhone || rawPhone;
          params.scope = tabStorage.phone.searchScope === 'global' ? 'global' : 'ro';
          if (hasAllToken) params.all = '1';
          break;
        }
        default:
          break;
      }
    }

    clearFeedExclusionsForActiveTab();
    const mergeIdentity = getStreamMergeIdentity(activeTab, tabStorage);
    const hasPrev =
      (tabStorage[activeTab].results || []).length > 0 || (tabStorage[activeTab].logs || []).length > 0;
    const lastId = lastStreamMergeIdentityRef.current[activeTab];
    const shouldMerge = Boolean(mergeIdentity && hasPrev && lastId != null && mergeIdentity === lastId);

    setTabStorage((prev) => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        ...(activeTab === TAB_PERSON && personResolved
          ? {
              input: personResolved.name,
              locationTags: personResolved.locationTags,
              contextTags: personResolved.contextTags,
              scanType: personResolved.scanIntent === 'deep' ? 'deep' : tabStorage.person.scanType || 'light',
            }
          : {}),
        ...(shouldMerge
          ? { completed: false }
          : {
              results: [],
              logs: [],
              completed: false,
              ...(activeTab === TAB_PHONE
                ? { extractedLeads: { emails: [], phones: [], cuis: [], associated_urls: [], associated_urls_partial: [], pivots: [] } }
                : {}),
            }),
      },
    }));
    setIsSearchingInTab(true);
    setActiveSearchTraceMeta('endpoint', endpoint);
    markSearchTrace('handle_search_start', { endpoint, detail: 'starting SSE' });
    const streamOpts = shouldMerge
      ? {
          mergeBaseResults: tabStorage[activeTab].results || [],
          mergeBaseLogs: tabStorage[activeTab].logs || [],
          appendLocationsLabel: buildAppendMergeLabel(activeTab, tabStorage),
        }
      : undefined;
    trackEvent('scan_initiated', {
      module: activeTab,
    });
    startStream(endpoint, params, streamOpts);
  }, [activeTab, tabStorage, loading, clearFeedExclusionsForActiveTab, startStream, resetPersonCategoryFilter]);

  const handleSearchRef = useRef(handleSearch);
  handleSearchRef.current = handleSearch;

  useEffect(() => {
    if (!pendingScan) return undefined;
    // Avoid setting pendingScan false synchronously here: that re-renders, runs
    // effect cleanup, and can cancel the scheduled handleSearch (Enter looked broken).
    const id = window.setTimeout(() => {
      setPendingScan(false);
      handleSearchRef.current();
    }, 0);
    return () => window.clearTimeout(id);
  }, [pendingScan]);

  const launchScanFromHero = useCallback(
    async (tab, query, launchOpts = {}) => {
      const q = String(query || '').trim();
      if (!q) return;

      if (looksLikeSystemCommandOrCode(q)) {
        const errTab = tab || TAB_PERSON;
        setTabStorage((prev) => ({
          ...prev,
          [errTab]: {
            ...(prev[errTab] || {}),
            input: q,
            inputError: SYSTEM_COMMAND_MESSAGE,
          },
        }));
        setActiveTab(errTab);
        setIsSearchingInTab(false);
        return;
      }

      if (
        (tab === TAB_PERSON || tab === TAB_COMPANY) &&
        isInsufficientSingleTermQuery(q)
      ) {
        const wantsCompany = tab === TAB_COMPANY;
        const corpTagsEarly =
          wantsCompany && launchOpts.companyTags && typeof launchOpts.companyTags === 'object'
            ? launchOpts.companyTags
            : null;
        const hostEarly =
          String(corpTagsEarly?.domain || '').trim() || companyDomainFromQuery(q);
        if (!hostEarly) {
          const errTab = tab || TAB_PERSON;
          setTabStorage((prev) => ({
            ...prev,
            [errTab]: {
              ...(prev[errTab] || {}),
              ...patchTabStorageForQuery(prev, errTab, q)[errTab],
              input: q,
              inputError: SINGLE_TERM_INSUFFICIENT_HINT,
            },
          }));
          setActiveTab(errTab);
          setIsSearchingInTab(false);
          return;
        }
      }

      const dnsVerifiedHosts = new Set(
        (Array.isArray(launchOpts.dnsVerifiedHosts) ? launchOpts.dnsVerifiedHosts : [])
          .map((h) => String(h || '').trim().toLowerCase())
          .filter(Boolean),
      );
      let effectiveTab = tab;
      let err = validateTabInputValue(tab, q);

      if (loading) cancelStream();
      resetStream();
      personStreamPrefetchRef.current = null;
      if (tab === TAB_PERSON || effectiveTab === TAB_PERSON) {
        resetPersonCategoryFilter();
      }

      if (tab === TAB_EMAIL) {
        const host = emailDomainFromAddress(q);
        if (host && !dnsVerifiedHosts.has(host)) {
          markSearchTrace('dns_check_start', { detail: 'launchScan email DNS' });
          const dnsOk = await checkDomainNS(host);
          markSearchTrace('dns_check_done', { detail: dnsOk ? 'ok' : 'no NS records' });
          if (!dnsOk) {
            setTabStorage((prev) => ({
              ...prev,
              email: {
                ...prev.email,
                input: q,
                inputError: EMAIL_DOMAIN_DNS_HINT,
              },
            }));
            setActiveTab(TAB_EMAIL);
            setIsSearchingInTab(false);
            return;
          }
        }
      }

      if (tab === TAB_COMPANY) {
        const corpTags =
          launchOpts.companyTags && typeof launchOpts.companyTags === 'object'
            ? launchOpts.companyTags
            : null;
        const host =
          String(corpTags?.domain || '').trim() || companyDomainFromQuery(q);
        if (!host) {
          setTabStorage((prev) => ({
            ...prev,
            company: {
              ...prev.company,
              ...patchTabStorageForQuery(prev, TAB_COMPANY, q).company,
              domain: '',
              inputError: CORPORATE_DOMAIN_REQUIRED_HINT,
            },
          }));
          setActiveTab(TAB_COMPANY);
          setIsSearchingInTab(false);
          return;
        }
        if (host && !dnsVerifiedHosts.has(host)) {
          markSearchTrace('dns_check_start', { detail: 'launchScan company DNS' });
          const dnsOk = await checkDomainNS(host);
          markSearchTrace('dns_check_done', { detail: dnsOk ? 'ok' : 'no NS records' });
          if (!dnsOk) {
            setTabStorage((prev) => ({
              ...prev,
              company: {
                ...prev.company,
                ...patchTabStorageForQuery(prev, TAB_COMPANY, q).company,
                domain: host || prev.company.domain,
                inputError: CORPORATE_DOMAIN_DNS_HINT,
              },
            }));
            setActiveTab(TAB_COMPANY);
            setIsSearchingInTab(false);
            return;
          }
        }
        if (corpTags) {
          const companyName = String(
            corpTags.streamName || corpTags.companyName || q,
          ).trim() || q;
          const companyDomain = String(corpTags.domain || host || '').trim();
          if (!companyDomain) {
            setTabStorage((prev) => ({
              ...prev,
              company: {
                ...prev.company,
                name: companyName,
                domain: '',
                inputError: CORPORATE_DOMAIN_REQUIRED_HINT,
              },
            }));
            setActiveTab(TAB_COMPANY);
            setIsSearchingInTab(false);
            return;
          }
          setTabStorage((prev) => ({
            ...prev,
            company: {
              ...prev.company,
              name: companyName,
              domain: companyDomain || prev.company.domain,
              country: companyDomain
                ? inferCompanyCountryFromDomain(companyDomain)
                : prev.company.country,
              locationTags: corpTags.locationTags || [],
              inputError: '',
            },
          }));
          err = validateTabInputValue(TAB_COMPANY, companyName);
          setActiveTab(TAB_COMPANY);
          setIsSearchingInTab(true);
          setPendingScan(true);
          return;
        }
      }

      if (tab === TAB_PERSON) {
        markSearchTrace('preprocess_start', { detail: 'hero routing / Ollama+spaCy' });
        const pillTags = launchOpts.personTags && typeof launchOpts.personTags === 'object'
          ? launchOpts.personTags
          : null;
        const existingFromPills = pillTags
          ? {
              locationTags: pillTags.locationTags || [],
              contextTags: pillTags.contextTags || [],
              usernameTags: pillTags.usernameTags || [],
            }
          : {};
        // Hero pills already carry a classified query — skip HTTP preprocess to start SSE immediately.
        let built;
        if (pillTags?.name) {
          const resolvedFromPills = {
            name: pillTags.name,
            locationTags: pillTags.locationTags || [],
            contextTags: pillTags.contextTags || [],
            username: pillTags.username || null,
            scanIntent: 'light',
            suggestedTab: 'person',
            preprocessSource: 'pills',
          };
          built = {
            resolved: resolvedFromPills,
            params: buildPersonStreamParamsFromResolved(resolvedFromPills, {
              ...existingFromPills,
              scanType: 'light',
            }),
          };
        } else {
          built = await buildPersonStreamParams(
            q,
            existingFromPills,
          );
        }
        // Prefer user-corrected pill classification over preprocess when present.
        if (pillTags && !built.resolved?.preprocessSource?.includes('pills')) {
          const mergedResolved = {
            ...built.resolved,
            name: pillTags.name || built.resolved.name,
            locationTags: pillTags.locationTags?.length
              ? pillTags.locationTags
              : built.resolved.locationTags,
            contextTags: pillTags.contextTags?.length
              ? pillTags.contextTags
              : built.resolved.contextTags,
            username: pillTags.username || built.resolved.username,
            contextCompany: built.resolved.contextCompany,
          };
          built.resolved = mergedResolved;
          built.params = buildPersonStreamParamsFromResolved(mergedResolved, {
            ...existingFromPills,
            scanType: mergedResolved.scanIntent === 'deep' ? 'deep' : 'light',
          });
        }
        markSearchTrace('preprocess_done', {
          detail: built.resolved?.preprocessSource || built.resolved?.suggestedTab || 'resolved',
        });
        const { resolved } = built;
        const scanType = resolved.scanIntent === 'deep' ? 'deep' : 'light';
        const respectManualTab = Boolean(launchOpts.respectManualTab);
        const wantsCompany = !respectManualTab && resolved.suggestedTab === TAB_COMPANY;

        if (wantsCompany) {
          effectiveTab = TAB_COMPANY;
          const companyName = String(
            resolved.streamParams?.name
              || resolved.hybrid?.stream_name
              || resolved.name
              || q,
          ).trim() || q;
          const companyDomain = String(
            resolved.companyDomain || resolved.streamParams?.domain || '',
          ).trim();
          if (!companyDomain && !companyDomainFromQuery(q)) {
            setTabStorage((prev) => {
              const patched = patchTabStorageForQuery(prev, TAB_COMPANY, companyName);
              return {
                ...patched,
                company: {
                  ...patched.company,
                  name: companyName,
                  domain: '',
                  inputError: CORPORATE_DOMAIN_REQUIRED_HINT,
                },
              };
            });
            setActiveTab(TAB_COMPANY);
            setIsSearchingInTab(false);
            return;
          }
          setTabStorage((prev) => {
            const patched = patchTabStorageForQuery(prev, TAB_COMPANY, companyName);
            return {
              ...patched,
              company: {
                ...patched.company,
                name: companyName,
                domain: companyDomain || patched.company.domain,
                country: companyDomain
                  ? inferCompanyCountryFromDomain(companyDomain)
                  : patched.company.country,
                locationTags: resolved.locationTags || [],
                inputError: '',
              },
            };
          });
        } else if (
          !respectManualTab
          && resolved.suggestedTab
          && resolved.suggestedTab !== TAB_PERSON
        ) {
          effectiveTab = resolved.suggestedTab;
          const patchQuery = resolved.name || q;
          setTabStorage((prev) => patchTabStorageForQuery(prev, effectiveTab, patchQuery));
        } else {
          personStreamPrefetchRef.current = built;
          setTabStorage((prev) => ({
            ...prev,
            person: {
              ...prev.person,
              input: resolved.name,
              locationTags: resolved.locationTags,
              contextTags: resolved.contextTags,
              usernameTags: pillTags?.usernameTags || prev.person.usernameTags || [],
              scanType,
              inputError: validateTabInputValue(TAB_PERSON, resolved.name) || '',
            },
          }));
        }
        err = validateTabInputValue(effectiveTab, q);
      } else {
        setTabStorage((prev) => patchTabStorageForQuery(prev, tab, q));
      }

      if (err) return;
      setActiveTab(effectiveTab);
      setIsSearchingInTab(true);
      setPendingScan(true);
    },
    [loading, cancelStream, resetStream, resetPersonCategoryFilter],
  );

  const handleLeadEmailAnalysis = useCallback((emailValue) => {
    const email = String(emailValue || '').trim();
    if (!email) return;
    openInvestigationInNewTab({ module: TAB_EMAIL, query: email });
  }, []);

  const handlePivotToPersonSearch = useCallback(
    ({ personName, companyName, domain, locations, contextTags } = {}) => {
      const name = String(personName || '').trim();
      if (!name) return;

      // Preserve corporate location / employer context in active Person queryTags.
      const corpLocs = Array.isArray(tabStorage.company?.locationTags)
        ? tabStorage.company.locationTags
        : [];
      const locTags = mergePersonTagLists(
        corpLocs,
        Array.isArray(locations) ? locations : [],
      );
      const employer = String(companyName || tabStorage.company?.name || '').trim();
      const domainHint = String(domain || tabStorage.company?.domain || '').trim();
      const ctxSeed = [
        ...(Array.isArray(contextTags) ? contextTags : []),
        employer,
        // Prefer brand/company name over raw domain for person context_focus.
      ].filter(Boolean);
      const ctxTags = mergePersonTagLists([], ctxSeed);

      if (loading) cancelStream();
      resetStream();
      resetPersonCategoryFilter();
      personStreamPrefetchRef.current = null;

      setTabStorage((prev) => ({
        ...prev,
        person: {
          ...prev.person,
          input: name,
          locationTags: locTags,
          contextTags: ctxTags,
          usernameTags: [],
          scanType: prev.person?.scanType || 'light',
          inputError: '',
          results: [],
          logs: [],
          completed: false,
          // Keep domain for diagnostics; stream params use context_focus from tags.
          domain: domainHint || prev.person?.domain || '',
        },
      }));
      setActiveTab(TAB_PERSON);
      setIsSearchingInTab(true);
      setPendingScan(true);
    },
    [
      tabStorage.company?.locationTags,
      tabStorage.company?.name,
      tabStorage.company?.domain,
      loading,
      cancelStream,
      resetStream,
      resetPersonCategoryFilter,
    ],
  );

  const handleDiscoveryPivotLead = useCallback(
    (pivot) => {
      const opts = deepLinkOptsFromPivot(pivot || {});
      if (!opts?.query) return;
      const module = opts.module;
      if (module === TAB_COMPANY) {
        launchScanFromHero(TAB_COMPANY, opts.query, {
          companyTags: {
            companyName: opts.name || opts.query,
            domain: opts.domain || '',
            locationTags: Array.isArray(opts.locations) ? opts.locations : [],
            streamName: opts.query,
          },
        });
        return;
      }
      if (module === TAB_PERSON) {
        const handle = String(opts.handle || '').trim().replace(/^@+/, '');
        const name = String(opts.name || opts.query || '').trim();
        launchScanFromHero(TAB_PERSON, opts.query, {
          respectManualTab: true,
          personTags: {
            name: name || opts.query,
            locationTags: Array.isArray(opts.locations) ? opts.locations : [],
            contextTags: [],
            usernameTags: handle ? [handle] : [],
            username: handle || null,
          },
        });
        return;
      }
      if (module === TAB_EMAIL) {
        launchScanFromHero(TAB_EMAIL, opts.query);
        return;
      }
      launchScanFromHero(module || TAB_PERSON, opts.query);
    },
    [launchScanFromHero],
  );

  const tabSlice = tabStorage[activeTab];

  const currentData = useMemo(() => {
    const displayResults = pickIntelRowsForDisplay({
      loading,
      completed,
      streamResults: results,
      storedResults: tabSlice.results,
    });
    const liveLogs = filterStreamLogRows(results);
    return {
      ...tabSlice,
      results: displayResults,
      logs: loading && liveLogs.length ? liveLogs : tabSlice.logs || [],
      completed: completed || tabSlice.completed,
    };
  }, [tabSlice, loading, results, completed]);

  const scanResultsForReview = useMemo(() => {
    const data = (currentData.results || []).filter((r) => r?.type !== 'cache_meta');
    const hasGhuntMaster = data.some(
      (r) =>
        (r.source === 'ghunt' && r.platform === 'Google Account Info') ||
        r.ui_bucket === 'ghunt_footprint',
    );
    if (hasGhuntMaster)
      return data.filter((r) => !(r.source === 'ghunt' && r.platform === 'Google Maps'));
    return data;
  }, [currentData.results]);

  const displayResults = useMemo(() => {
    let rows = scanResultsForReview.filter(
      (r) => !feedHiddenKeys.has(`${activeTab}:${reviewResultKey(r)}`),
    );
    if (activeTab === TAB_PERSON) {
      rows = rows.filter((r) => !isPersonCensysResult(r));
    }
    return rows;
  }, [scanResultsForReview, activeTab, feedHiddenKeys]);

  const stats = useMemo(() => computeStats(displayResults, loading, activeTab), [displayResults, loading, activeTab]);

  const discoveryCategories = useMemo(() => {
    const b = { Work: 0, Social: 0, Security: 0 };
    if (activeTab === TAB_COMPANY) {
      displayResults.forEach((r) => {
        const ub = r.ui_bucket || '';
        if (ub === 'cyber_trust_score' || ub === 'threat_intelligence' || ub === 'compliance_alert' || ub === 'wayback_archive' || ub === 'email_deliverability' || ub === 'registered_service' || ub === 'ghunt_footprint' || ub === 'darkweb_advisory' || ub === 'hibp_status') b.Security += 1;
        else if (ub === 'relationship_node' || ub === 'ubo_network' || ub === 'legal_records') b.Work += 1;
        else b.Social += 1;
      });
      return b;
    }
    displayResults.forEach((r) => {
      const cat = r.category;
      if (cat === 'Work' || cat === 'Social' || cat === 'Security') b[cat] += 1;
      else b.Social += 1;
    });
    return b;
  }, [displayResults, activeTab]);

  const hasVerifiedIdentity = useMemo(
    () => displayResults.some((r) => r.is_primary_identity === true),
    [displayResults],
  );

  const radarSummary = useMemo(() => {
    if (activeTab !== TAB_PERSON || !currentData.completed || loading) return '';
    const primary = displayResults.find(
      (r) => r.is_primary_identity === true && Array.isArray(r.found_context) && r.found_context.length > 0,
    );
    if (!primary) return '';
    const fc = primary.found_context.filter((c) => typeof c === 'string' && c.trim());
    const company =
      fc.find((c) => /\b(srl|sa|s\.a\.|ltd|inc|llc|gmbh|choice)\b/i.test(c)) ||
      fc.find((c) => c.length > 4) ||
      fc[0];
    return company ? `Identity confirmed at ${company.trim()}` : '';
  }, [activeTab, currentData.completed, loading, displayResults]);

  const scanFinishedRadar = useMemo(
    () => currentData.completed && !loading && !hasVerifiedIdentity,
    [currentData.completed, loading, hasVerifiedIdentity],
  );

  const reportTarget = useMemo(() => {
    if (activeTab === TAB_COMPANY) {
      const n = tabStorage.company.name || '';
      const d = tabStorage.company.domain;
      return d ? `${n} (${d})` : n;
    }
    return currentData.input || '';
  }, [activeTab, tabStorage.company.name, tabStorage.company.domain, currentData.input]);

  const showResultsArea = isSearchingInTab || displayResults.length > 0 || loading;

  const hasAnyOutput = useMemo(
    () => (currentData.results?.length || 0) > 0 || (currentData.logs?.length || 0) > 0,
    [currentData.results, currentData.logs],
  );

  const hasWorkspaceInput = useMemo(() => {
    if (activeTab === TAB_COMPANY) {
      return Boolean(
        tabStorage.company.name ||
          tabStorage.company.domain ||
          (tabStorage.company.locationTags?.length || 0) > 0,
      );
    }
    if (activeTab === TAB_PERSON) {
      return Boolean(
        currentData.input ||
          (tabStorage.person.locationTags?.length || 0) > 0 ||
          (tabStorage.person.contextTags?.length || 0) > 0 ||
          (tabStorage.person.usernameTags?.length || 0) > 0,
      );
    }
    return Boolean(currentData.input);
  }, [activeTab, tabStorage, currentData.input]);

  const cancelSearch = useCallback(() => {
    abortScan();
  }, [abortScan]);

  const canReport = currentData.completed && !loading;

  const resultsCount = useMemo(
    () => countStreamIntelRows(displayResults),
    [displayResults],
  );

  const handleResetClick = useCallback(
    ({ hasHeroQuery = false, clearHeroQuery } = {}) => {
      const hasAnyInput = hasWorkspaceInput || hasHeroQuery;
      if (hasAnyOutput) {
        handleClearOutputOnly();
        resetArmedRef.current[activeTab] = true;
        return;
      }
      if (resetArmedRef.current[activeTab] && hasAnyInput) {
        handleResetModule();
        clearHeroQuery?.();
        return;
      }
      if (hasAnyInput) {
        handleResetModule();
        clearHeroQuery?.();
      }
    },
    [activeTab, hasAnyOutput, hasWorkspaceInput, handleClearOutputOnly, handleResetModule],
  );

  const resetWorkspaceFully = useCallback(
    ({ clearHeroQuery } = {}) => {
      resetArmedRef.current = { person: false, email: false, phone: false, company: false };
      handleResetModule();
      resetPersonCategoryFilter();
      setIsReportModalOpen(false);
      clearHeroQuery?.();
    },
    [handleResetModule, resetPersonCategoryFilter],
  );

  return {
    activeTab,
    handleTabChange,
    launchScanFromHero,
    cancelSearch,
    abortScan,
    handleResetClick,
    resetWorkspaceFully,
    hasAnyOutput,
    hasWorkspaceInput,
    handleLeadEmailAnalysis,
    handlePivotToPersonSearch,
    handleDiscoveryPivotLead,
    loading,
    canReport,
    completed,
    currentData,
    displayResults,
    resultsCount,
    scanResultsForReview,
    stats,
    discoveryCategories,
    hasVerifiedIdentity,
    radarSummary,
    scanFinishedRadar,
    reportTarget,
    showResultsArea,
    isSearchingInTab,
    personCategoryFilter,
    setPersonCategoryFilter,
    getDisplayName: getDisplayNameForResult,
    excludeResultFromFeed,
    isReportModalOpen,
    setIsReportModalOpen,
    logEndRef,
  };
}
